'use strict';

import Button from './button.js';
import Tile from './tile.js';
import TileListController from './tileListController.js';
import TileListView from './tileListView.js';
import scrollModule from './scroll.js';
import {Logger} from "../../logger";
import TrackingService from "./tracking_service";

export default class Cinema {

    constructor(cinemaDom, config) {
        this.cinemaDom = cinemaDom;
        this.config = config;
        this.trackingService = new TrackingService();
        this.touchStartXPositon = null;

        this.featureOrder = this.getFeatureOrder(cinemaDom);
        this.masFeatureTrackingLabels = this.getMasFeatureTrackingLabels(cinemaDom);
        this.featureIndex = this.getFeatureIndex(cinemaDom);
        this.personalizationContext = this.getPersonalizationContext(cinemaDom);

        const tiles = Array.from(cinemaDom.querySelectorAll(config.tileSelector)).map((tileDom) => new Tile(tileDom));
        [].map.call(tiles, tile => {
            tile.tileDom.addEventListener("click", () => {
                this.trackingService.sendSmallShoppromoClickTrackingEvent(this.featureOrder, this.featureIndex, this.personalizationContext, tiles.length, tile, this.masFeatureTrackingLabels)
            });
        });

        this.tiles = tiles;
        this.tileListViewDomObject = cinemaDom.querySelector(config.containerSelector);
        this.tileListView = new TileListView(this.tileListViewDomObject, scrollModule(), () => {
            Logger.log("Tiles:", tiles);
            return tiles[0].getWidth() + (config.tilePaddingRight || 0)
        });
        const tileListView = this.tileListView;
        this.tileListController = new TileListController(tileListView, tiles);

        window.addEventListener('resize', () => this.updateButtonVisibility());

        this.leftButton = new Button(cinemaDom.querySelector(config.buttonLeftSelector), config.buttonHideClass);
        this.leftButton.registerOnClickHandler(() => {
            this.tileListController.scrollLeft(() => {
                this.updateButtonVisibility();
            });
            setTimeout(() => {
                this.trackingService.sendViewTrackingEventForCinemaMovement(this.featureOrder, this.featureIndex, this.tiles, this.personalizationContext,
                    tileListView.mostVisibleTileIndex(), tileListView.lastVisibleTileIndex(), "previous", this.masFeatureTrackingLabels);
            }, 300)
        });
        this.rightButton = new Button(cinemaDom.querySelector(config.buttonRightSelector), config.buttonHideClass);
        this.rightButton.registerOnClickHandler(() => {
            this.tileListController.scrollRight(() => {
                this.updateButtonVisibility();
            });
            setTimeout(() => {
                this.trackingService.sendViewTrackingEventForCinemaMovement(this.featureOrder, this.featureIndex, this.tiles, this.personalizationContext,
                    tileListView.mostVisibleTileIndex(), tileListView.lastVisibleTileIndex(), "next", this.masFeatureTrackingLabels);
            }, 300)
        });

        tileListView.registerOnTouchStartListener((event) => {
            if (event.changedTouches.length > 0) {
                this.touchStartXPositon = event.changedTouches[0].screenX
            }
        })

        tileListView.registerOnTouchEndListener((event) => {
            if (this.touchStartXPositon && event.changedTouches.length > 0
                && this.touchStartXPositon !== event.changedTouches[0].screenX) {
                const movementType = this.touchStartXPositon < event.changedTouches[0].screenX ? "previous" : "next";
                this.trackingService.sendViewTrackingEventForCinemaMovement(this.featureOrder, this.featureIndex, this.tiles, this.personalizationContext,
                    tileListView.mostVisibleTileIndex(), tileListView.lastVisibleTileIndex(), movementType, this.masFeatureTrackingLabels);
                this.touchStartXPositon = null;
            }
        });

        this.addMouseMovementListenerForHybridDevices();
        this.waitForCss();
    }

    waitForCss() {
        const self = this;
        let isAlreadyTracked = false;
        const trackingTimeout = setTimeout(() => {
            /*🤫*/
            Logger.log("Send initial fake view tracking although css seems not to be loaded.");
            this.sendInitialViewTracking(self, 2);
            isAlreadyTracked = true;
        }, 5000);
        const intervalId = window.setInterval(() => {
            const scrollWidth = self.tileListViewDomObject.scrollWidth;
            const width = self.tileListViewDomObject.getBoundingClientRect().width || self.tileListViewDomObject.offsetWidth;
            const widthOfLastTile = this.tiles[this.tiles.length - 1].getWidth();
            /*🤫*/
            Logger.log("Small Cinema - scrollWidth: " + scrollWidth + " - offsetWidth: " + width + " - document.hidden: " + document.hidden + " - timestamp: " + Date.now() + " - last tile width: " + widthOfLastTile);
            /*🤫*/
            if (scrollWidth > 0 && !document.hidden && widthOfLastTile < scrollWidth) { /*🤫*/
                Logger.log("Updating button visibility and clearing interval.");
                self.updateButtonVisibility();
                if (!isAlreadyTracked) {
                    clearTimeout(trackingTimeout);
                    this.sendInitialViewTracking(self, self.tileListView.fullyVisibleTilesPerPage());
                } else {
                    Logger.log("Skip initial view tracking because there was already a fake view tracking.");
                }
                window.clearInterval(intervalId);
            }
        }, 100);
    }

    sendInitialViewTracking(self, visibleTiles) {
        self.trackingService.sendSmallShoppromoInitialViewTrackingEvent(this.featureOrder, this.featureIndex, this.personalizationContext, this.tiles, visibleTiles, this.masFeatureTrackingLabels);
    }

    updateButtonVisibility() {
        if (this.tileListController.isAtLeftBorder()) {
            this.leftButton.hide();
        } else {
            this.leftButton.show();
        }

        if (this.tileListController.isAtRightBorder()) {
            this.rightButton.hide();
        } else {
            this.rightButton.show();
        }
    }

    /*🤫*/
    addMouseMovementListenerForHybridDevices() {
        const self = this;
        const forceButtonVisibility = function onFirstHover() {
            self.leftButton.forceVisibility();
            self.rightButton.forceVisibility();
            self.cinemaDom.removeEventListener('mouseover', onFirstHover, false);
        };
        this.cinemaDom.addEventListener('mouseover', forceButtonVisibility, false);
        /*🤫*/
        this.cinemaDom.addEventListener('touchstart', function onFirstTouchStart() {
            self.cinemaDom.removeEventListener('mouseover', forceButtonVisibility, false);
            self.cinemaDom.removeEventListener('touchstart', onFirstTouchStart, false);
        }, {passive: true});
    }

    getFeatureOrder(container) {
        return parseInt(container.parentElement.parentElement.getAttribute('data-feature-order'), 0);
    }

    getMasFeatureTrackingLabels(container) {
        const rawMasFeatureTrackingLabels = container.parentElement.parentElement.getAttribute('data-feature-tracking-labels');
        return rawMasFeatureTrackingLabels ? JSON.parse(rawMasFeatureTrackingLabels) : null;
    }

    getFeatureIndex(container) {
        return parseInt(container.parentElement.getAttribute('data-feature-index'), 0);
    }

    getPersonalizationContext(container) {
        return container.parentElement.getAttribute('data-context')
    }
}